/**
 * DashboardManager — User dashboard overlay with order history and profile management.
 * Follows the FaqModal/HeroThicknessModal DOM-building pattern.
 */
export default class DashboardManager {
  constructor(auth, firebaseService) {
    this.auth = auth;
    this.firebase = firebaseService;
    this.overlay = null;
    this.orders = [];
    this.loading = false;

    this._buildOverlay();
  }

  show() {
    if (!this.auth.isLoggedIn()) return;
    this._updateUserInfo();
    this.overlay.classList.remove('hidden');
    this._loadOrders();
  }

  hide() {
    this.overlay.classList.add('hidden');
  }

  // --- DOM Construction ---

  _buildOverlay() {
    this.overlay = document.createElement('div');
    this.overlay.className = 'dashboard-overlay hidden';

    const panel = document.createElement('div');
    panel.className = 'dashboard-panel';

    // Header
    const header = document.createElement('div');
    header.className = 'dashboard-header';
    header.innerHTML = `
      <button class="dashboard-back" id="dashboard-back">
        <i class="fas fa-arrow-left"></i>
        <span>Zurück zum Konfigurator</span>
      </button>
    `;

    // User info section
    this.userSection = document.createElement('div');
    this.userSection.className = 'dashboard-user';
    this.userSection.innerHTML = `
      <div class="dashboard-avatar" id="dashboard-avatar"></div>
      <div class="dashboard-user-info">
        <div class="dashboard-user-name" id="dashboard-user-name"></div>
        <div class="dashboard-user-meta" id="dashboard-user-meta"></div>
      </div>
    `;

    // Orders section
    const ordersSection = document.createElement('div');
    ordersSection.className = 'dashboard-section';
    ordersSection.innerHTML = `
      <div class="dashboard-section-header">
        <span class="dashboard-section-title">Meine Anfragen</span>
        <span class="dashboard-order-count" id="dashboard-order-count"></span>
      </div>
    `;

    this.ordersList = document.createElement('div');
    this.ordersList.className = 'dashboard-orders';
    this.ordersList.id = 'dashboard-orders';
    this.ordersList.innerHTML = '<div class="dashboard-loading"><i class="fas fa-circle-notch fa-spin"></i></div>';
    ordersSection.appendChild(this.ordersList);

    // Profile section
    const profileSection = document.createElement('div');
    profileSection.className = 'dashboard-section';
    profileSection.innerHTML = `
      <div class="dashboard-section-header">
        <span class="dashboard-section-title">Profil bearbeiten</span>
      </div>
      <div class="dashboard-profile-form">
        <div class="dashboard-form-group">
          <label class="dashboard-form-label">Name</label>
          <input class="dashboard-form-input" type="text" id="dashboard-name" placeholder="Vor- und Nachname">
        </div>
        <div class="dashboard-form-group">
          <label class="dashboard-form-label">Firma</label>
          <input class="dashboard-form-input" type="text" id="dashboard-company" placeholder="Firma">
        </div>
        <div class="dashboard-form-group">
          <label class="dashboard-form-label">E-Mail</label>
          <input class="dashboard-form-input" type="email" id="dashboard-email" disabled>
        </div>
        <div class="dashboard-form-actions">
          <button class="dashboard-btn-secondary" id="dashboard-change-pw">
            <i class="fas fa-key"></i>
            <span>Passwort ändern</span>
          </button>
          <button class="dashboard-btn-primary" id="dashboard-save-profile">
            <span>Speichern</span>
          </button>
        </div>
        <div class="dashboard-form-message hidden" id="dashboard-profile-msg"></div>
      </div>
      <div class="dashboard-pw-form hidden" id="dashboard-pw-form">
        <div class="dashboard-form-group">
          <label class="dashboard-form-label">Aktuelles Passwort</label>
          <input class="dashboard-form-input" type="password" id="dashboard-current-pw">
        </div>
        <div class="dashboard-form-group">
          <label class="dashboard-form-label">Neues Passwort</label>
          <input class="dashboard-form-input" type="password" id="dashboard-new-pw" placeholder="Mind. 8 Zeichen">
        </div>
        <div class="dashboard-form-actions">
          <button class="dashboard-btn-secondary" id="dashboard-cancel-pw">Abbrechen</button>
          <button class="dashboard-btn-primary" id="dashboard-submit-pw">Passwort ändern</button>
        </div>
        <div class="dashboard-form-message hidden" id="dashboard-pw-msg"></div>
      </div>
    `;

    panel.appendChild(header);
    panel.appendChild(this.userSection);
    panel.appendChild(ordersSection);
    panel.appendChild(profileSection);

    this.overlay.appendChild(panel);
    document.body.appendChild(this.overlay);

    this._bindEvents();
  }

  _bindEvents() {
    // Back button
    this.overlay.querySelector('#dashboard-back').addEventListener('click', () => this.hide());

    // Click overlay backdrop to close
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.hide();
    });

    // Save profile
    this.overlay.querySelector('#dashboard-save-profile').addEventListener('click', () => this._saveProfile());

    // Change password toggle
    this.overlay.querySelector('#dashboard-change-pw').addEventListener('click', () => {
      this.overlay.querySelector('#dashboard-pw-form').classList.remove('hidden');
      this.overlay.querySelector('#dashboard-change-pw').classList.add('hidden');
    });

    this.overlay.querySelector('#dashboard-cancel-pw').addEventListener('click', () => {
      this.overlay.querySelector('#dashboard-pw-form').classList.add('hidden');
      this.overlay.querySelector('#dashboard-change-pw').classList.remove('hidden');
    });

    // Submit password change
    this.overlay.querySelector('#dashboard-submit-pw').addEventListener('click', () => this._changePassword());

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.overlay.classList.contains('hidden')) {
        this.hide();
      }
    });
  }

  // --- Data Loading ---

  _updateUserInfo() {
    const user = this.auth.getUser();
    if (!user) return;

    const avatar = this.overlay.querySelector('#dashboard-avatar');
    const name = this.overlay.querySelector('#dashboard-user-name');
    const meta = this.overlay.querySelector('#dashboard-user-meta');

    if (avatar) avatar.textContent = this._getInitials(user.name || user.email);
    if (name) name.textContent = user.name || user.email;
    if (meta) {
      const parts = [user.email];
      if (user.company) parts.push(user.company);
      meta.textContent = parts.join(' · ');
    }

    // Pre-fill profile form
    const nameInput = this.overlay.querySelector('#dashboard-name');
    const companyInput = this.overlay.querySelector('#dashboard-company');
    const emailInput = this.overlay.querySelector('#dashboard-email');
    if (nameInput) nameInput.value = user.name || '';
    if (companyInput) companyInput.value = user.company || '';
    if (emailInput) emailInput.value = user.email;
  }

  async _loadOrders() {
    const user = this.auth.getUser();
    if (!user || !this.firebase) return;

    this.ordersList.innerHTML = '<div class="dashboard-loading"><i class="fas fa-circle-notch fa-spin"></i></div>';

    try {
      this.orders = await this.firebase.getUserOrders(user.email);
      this._renderOrders();
    } catch (e) {
      console.error('Failed to load orders:', e);
      this.ordersList.innerHTML = '<div class="dashboard-empty">Fehler beim Laden der Anfragen.</div>';
    }
  }

  _renderOrders() {
    const count = this.overlay.querySelector('#dashboard-order-count');
    if (count) count.textContent = `(${this.orders.length})`;

    if (this.orders.length === 0) {
      this.ordersList.innerHTML = `
        <div class="dashboard-empty">
          <i class="fas fa-inbox"></i>
          <p>Noch keine Anfragen gesendet.</p>
        </div>
      `;
      return;
    }

    this.ordersList.innerHTML = '';
    this.orders.forEach(order => {
      const item = this._buildOrderItem(order);
      this.ordersList.appendChild(item);
    });
  }

  _buildOrderItem(order) {
    const item = document.createElement('div');
    item.className = 'dashboard-order-item';

    const posCount = (order.positions || []).length;
    const totalSqm = (order.totalSqm || 0).toFixed(1);
    const date = order.createdAt?.toDate
      ? order.createdAt.toDate().toLocaleDateString('de-CH')
      : order.createdAt?.seconds
        ? new Date(order.createdAt.seconds * 1000).toLocaleDateString('de-CH')
        : '—';

    const statusMap = {
      'new': 'Neu',
      'processing': 'In Bearbeitung',
      'completed': 'Abgeschlossen',
      'cancelled': 'Storniert'
    };
    const statusLabel = statusMap[order.status] || order.status || 'Neu';
    const statusClass = order.status || 'new';

    item.innerHTML = `
      <div class="order-item-header">
        <div class="order-item-code">${order.code || order.id}</div>
        <div class="order-item-date">${date}</div>
      </div>
      <div class="order-item-summary">
        <span>${posCount} Position${posCount !== 1 ? 'en' : ''}</span>
        <span class="order-item-dot">·</span>
        <span>${totalSqm} m²</span>
        <span class="order-item-dot">·</span>
        <span class="order-item-status status-${statusClass}">${statusLabel}</span>
      </div>
      ${order.kommission ? `<div class="order-item-kommission">Kommission: ${order.kommission}</div>` : ''}
      <div class="order-item-details hidden" id="order-details-${order.id}"></div>
      <div class="order-item-actions">
        <button class="order-item-btn order-toggle-details" data-order-id="${order.id}">
          <i class="fas fa-chevron-down"></i>
          <span>Details</span>
        </button>
      </div>
    `;

    // Toggle details
    item.querySelector('.order-toggle-details').addEventListener('click', (e) => {
      const btn = e.currentTarget;
      const details = item.querySelector('.order-item-details');
      const icon = btn.querySelector('i');
      const label = btn.querySelector('span');

      if (details.classList.contains('hidden')) {
        details.classList.remove('hidden');
        icon.className = 'fas fa-chevron-up';
        label.textContent = 'Schliessen';
        this._renderOrderDetails(details, order);
      } else {
        details.classList.add('hidden');
        icon.className = 'fas fa-chevron-down';
        label.textContent = 'Details';
      }
    });

    return item;
  }

  _renderOrderDetails(container, order) {
    if (!order.positions || order.positions.length === 0) {
      container.innerHTML = '<div class="order-detail-empty">Keine Positionen</div>';
      return;
    }

    let html = '<div class="order-positions">';
    order.positions.forEach((pos, idx) => {
      const variants = (pos.variants || []).map(v =>
        `<div class="order-variant">${v.quantity}x ${v.width}×${v.height} mm</div>`
      ).join('');

      html += `
        <div class="order-position">
          <div class="order-position-header">Position ${pos.nr || idx + 1}</div>
          <div class="order-position-glass">${pos.frgName || '—'}</div>
          <div class="order-position-specs">
            ${pos.class || ''} · ${pos.structure || ''} · ${pos.insulation || ''}
          </div>
          <div class="order-position-variants">${variants}</div>
        </div>
      `;
    });
    html += '</div>';

    if (order.notiz) {
      html += `<div class="order-note"><strong>Notiz:</strong> ${order.notiz}</div>`;
    }

    container.innerHTML = html;
  }

  // --- Profile Actions ---

  async _saveProfile() {
    const name = this.overlay.querySelector('#dashboard-name').value.trim();
    const company = this.overlay.querySelector('#dashboard-company').value.trim();
    const msgEl = this.overlay.querySelector('#dashboard-profile-msg');

    try {
      await this.firebase.updateUserProfile(name, company);
      // Update local auth state
      if (this.auth.user) {
        this.auth.user.name = name;
        this.auth.user.company = company;
      }
      this.auth._updateAuthButtons();
      this._updateUserInfo();
      this._showMessage(msgEl, 'Profil gespeichert.', 'success');
    } catch (e) {
      this._showMessage(msgEl, 'Fehler beim Speichern.', 'error');
    }
  }

  async _changePassword() {
    const currentPw = this.overlay.querySelector('#dashboard-current-pw').value;
    const newPw = this.overlay.querySelector('#dashboard-new-pw').value;
    const msgEl = this.overlay.querySelector('#dashboard-pw-msg');

    if (!currentPw || !newPw) {
      this._showMessage(msgEl, 'Bitte beide Felder ausfüllen.', 'error');
      return;
    }

    if (newPw.length < 6) {
      this._showMessage(msgEl, 'Neues Passwort muss mindestens 6 Zeichen lang sein.', 'error');
      return;
    }

    try {
      await this.firebase.changePassword(currentPw, newPw);
      this._showMessage(msgEl, 'Passwort geändert.', 'success');
      this.overlay.querySelector('#dashboard-current-pw').value = '';
      this.overlay.querySelector('#dashboard-new-pw').value = '';
      setTimeout(() => {
        this.overlay.querySelector('#dashboard-pw-form').classList.add('hidden');
        this.overlay.querySelector('#dashboard-change-pw').classList.remove('hidden');
      }, 1500);
    } catch (e) {
      const msg = e.code === 'auth/wrong-password'
        ? 'Aktuelles Passwort ist falsch.'
        : 'Fehler beim Ändern des Passworts.';
      this._showMessage(msgEl, msg, 'error');
    }
  }

  _showMessage(el, text, type) {
    if (!el) return;
    el.textContent = text;
    el.className = `dashboard-form-message ${type}`;
    el.classList.remove('hidden');
    setTimeout(() => el.classList.add('hidden'), 3000);
  }

  _getInitials(str) {
    if (!str) return '?';
    const parts = str.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return str.substring(0, 2).toUpperCase();
  }
}
